import React, { FunctionComponent, useEffect } from 'react'
import { LogOnMount, useAmplitude } from 'react-amplitude-hooks'
import { useRoutePageName } from '../../config/router'
import { useConsumerSelector, useCountry, useRegistration } from '../../state'
import { setDatadogMetadata } from '@afterpaytouch/integrations/datadog'
import { datadogRum } from '@datadog/browser-rum'
import { useEnduringEventProperties } from '../amplitude/hooks'
import { PageNames, viewedPageEventName } from '../../model/monitoring'
import { isUndefinedOrEmpty } from '@afterpay/utils'

export interface TrackingProps {
  eventName: string
  eventProperties?: object
}

export const TrackPageViewed: FunctionComponent<{ pageName?: PageNames; eventProperties?: object }> = ({ pageName, eventProperties }) => {
  const routePageName = useRoutePageName()
  const eventPageName = pageName ?? routePageName
  const amplitudeProperties = useEnduringEventProperties()
  const country = useCountry()

  const eventName = viewedPageEventName(eventPageName)
  const combinedProperties = { ...amplitudeProperties, ...eventProperties, eventPageName }

  useEffect(() => {
    setDatadogMetadata({ country, ...combinedProperties })
  }, [country, amplitudeProperties])

  useEffect(() => {
    datadogRum.addAction(eventName, combinedProperties)
  }, [])

  return <LogOnMount eventType={eventName} eventProperties={combinedProperties} />
}

export const TrackOnMount: FunctionComponent<{ eventName: string; eventProperties?: object }> = ({ eventName, eventProperties }) => {
  const amplitudeProperties = useEnduringEventProperties()
  const country = useCountry()
  const { email, mobile } = useConsumerSelector()

  const combinedProperties = { ...amplitudeProperties, isMobileEmpty: isUndefinedOrEmpty(mobile), isEmailEmpty: isUndefinedOrEmpty(email), ...eventProperties }

  useEffect(() => {
    setDatadogMetadata({ country, ...combinedProperties })
  }, [country, eventProperties])

  useEffect(() => {
    datadogRum.addAction(eventName, combinedProperties)
  }, [])

  return <LogOnMount eventType={eventName} eventProperties={combinedProperties} />
}

export const useMonitoringWithEnduringEventProperties = (
  eventProperties: object = {}
): {
  logEvent: <T extends string>(eventType: T, eventProperties?: object) => void
  instrument: <T extends (...args: unknown[]) => unknown>(eventType: string, func: T) => T
} => {
  const enduringEventProperties = useEnduringEventProperties()
  const country = useCountry()
  const { id: registrationId } = useRegistration()

  const combinedProperties = { ...enduringEventProperties, ...eventProperties, registrationId }

  useEffect(() => {
    setDatadogMetadata({ country, ...combinedProperties })
  }, [country, enduringEventProperties])

  const { logEvent: amplitudeLogEvent, instrument: amplitudeInstrument } = useAmplitude(combinedProperties)

  const logEvent = <T extends string>(eventType: T, eventProperties?: object): void => {
    amplitudeLogEvent(eventType, eventProperties)
    datadogRum.addAction(eventType, eventProperties)
  }

  const instrument = <T extends (...args: unknown[]) => unknown>(eventType: string, func: T): T => {
    const wrappedInstrument = (...params: unknown[]): unknown => {
      datadogRum.addAction(eventType)

      // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
      const wrappedFunction = amplitudeInstrument(eventType, func as Function)
      return wrappedFunction(...params)
    }

    return wrappedInstrument as T
  }

  return { logEvent, instrument }
}

export const logCustomMonitoringEvent = (eventType: string, eventProperties?: object, type: 'ACTION' | 'ERROR' = 'ACTION'): void => {
  if (type === 'ACTION') {
    datadogRum.addAction(eventType, eventProperties)
  } else if (type === 'ERROR') {
    datadogRum.addError(eventType, eventProperties)
  }
}
